'use client';

import Image from 'next/image';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { CalmSleepLogo, CalmSleepLogoColor, FontWeights } from '@calm-web/design-system';

import appImage from '@/../public/_n/images/calm-sleep-app/handoff/app-image.png';
import qrCodeImageDev from '@/../public/_n/images/calm-sleep-app/handoff/qr-code-image-dev.png';
import qrCodeImage from '@/../public/_n/images/calm-sleep-app/handoff/qr-code-image.png';
import contentBasedImage from '@/../public/_n/images/calm-sleep-app/payment/content_based_image.png';
import messages from '@/app/(sleep)/calm-sleep-app/_messages';
import {
	getSubscriptionEventProps,
	useFormatPrice,
	useFormatPricePlusApplicableTaxes,
	useSisu,
} from '@/components/Sisu';
import * as Sisu from '@/components/Sisu';
import { useAuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import getUrl from '@/utils/getUrl';

export const useValueProps = (): Sisu.ValueProp[] => {
	const { formatMessage } = useIntl();

	const regularWeight = (chunk: string[]): JSX.Element => (
		<span style={{ fontWeight: FontWeights.Regular }}>{chunk}</span>
	);

	return [
		{ header: formatMessage(messages.valueProp1, { regularWeight }) },
		{ header: formatMessage(messages.valueProp2, { regularWeight }) },
		{ header: formatMessage(messages.valueProp3, { regularWeight }) },
	];
};

export const ContentBasedImage = () => <Sisu.ContentBasedImage src={contentBasedImage} />;

export const Header = () => (
	<Sisu.Header
		hideExitButton
		logo={
			<CalmSleepLogo color={CalmSleepLogoColor.Gradient} data-testid="header-calm-sleep-logo" height={45} />
		}
	/>
);

export const AccountScreen = () => {
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const { getSkuByDuration, selectedSku } = useSisu();
	const formatPrice = useFormatPrice();
	const valueProps = useValueProps();

	const yearlySku = getSkuByDuration('year');
	const monthlySku = getSkuByDuration('month');

	let authPromptToggle;
	let authFormTitle;
	let authFormSubtitle;
	let valuePropsListTitle;

	if (selectedSku.discount) {
		authPromptToggle = null;
		authFormTitle = formatMessage(messages.loginFormHeadingDiscounted, {
			originalPrice: () => (
				<s style={{ color: '#E14942' }}>{formatPrice(selectedSku.basePrice, selectedSku.currency)}</s>
			),
			currentPrice: formatPrice(selectedSku.finalPrice, selectedSku.currency),
		});
		authFormSubtitle = formatMessage(messages.accountSubheadingDiscounted, {
			a: chunk => <a href={getUrl('calm-sleep-app')}>{chunk}</a>,
		});
		valuePropsListTitle = formatMessage(messages.valuePropHeaderDiscounted);
	} else {
		authFormTitle = formatMessage(
			authPrompt === 'login' ? messages.loginFormHeading : messages.signupFormHeading,
		);
		authFormSubtitle = formatMessage(messages.accountSubheadingMonthly, {
			br: () => <br />,
			yearlyPrice: yearlySku ? formatPrice(yearlySku.finalPrice, yearlySku.currency) : undefined,
			monthlyPrice: monthlySku ? formatPrice(monthlySku.finalPrice, monthlySku.currency) : undefined,
		});
		valuePropsListTitle = formatMessage(messages.valuePropHeader);
	}

	return (
		<Sisu.AccountScreen
			authForm={
				<Sisu.AuthForm
					authPromptToggle={authPromptToggle}
					authPromptTogglePosition="below"
					title={authFormTitle}
					subtitle={authFormSubtitle}
				/>
			}
			header={<Header />}
			valuePropsList={<Sisu.ValuePropsList title={valuePropsListTitle} valueProps={valueProps} />}
		/>
	);
};

export const PaymentScreen = () => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const { hasFreeTrial, selectedSku } = useSisu();
	const formatPricePlusApplicableTaxes = useFormatPricePlusApplicableTaxes();
	const valueProps = useValueProps();

	const stripePublishableKey = process.env.NEXT_PUBLIC_SLEEP_STRIPE_TOKEN;

	const termsValues = {
		price: formatPricePlusApplicableTaxes(selectedSku.finalPrice, selectedSku.currency, selectedSku.country),
		cancelAnytimeLink: (chunk: string[]): JSX.Element => (
			<Sisu.CancelAnytimeLink>{chunk}</Sisu.CancelAnytimeLink>
		),
	};

	let terms;
	let title;
	let paymentPlan;
	if (selectedSku.discount) {
		terms = formatMessage(messages.trialTermsDiscounted, termsValues);
		title = formatMessage(messages.paymentHeading);
		paymentPlan = <Sisu.PaymentPlan title={formatMessage(messages.paymentFormHeader)} />;
	} else {
		terms = selectedSku.trialDuration
			? formatMessage(messages.trialTerms, termsValues)
			: formatMessage(messages.noTrialTerms, termsValues);
		title = formatMessage(
			selectedSku.duration === 'year' ? messages.paymentHeading : messages.paymentHeadingMonthly,
		);
	}

	// set undefined for free trial to fall back to default message
	const submitButtonLabel = hasFreeTrial ? undefined : formatMessage(messages.ctaCopy);

	return (
		<Sisu.PaymentScreen
			contentBasedImage={<ContentBasedImage />}
			header={<Header />}
			onScreenDidMount={() => {
				logEvent({
					eventName: 'Subscription : Upsell : Seen',
					eventProps: {
						...getSubscriptionEventProps(selectedSku),
						upsell_location: 'Web',
					},
					hydrateSleepEventProps: true,
				});
			}}
			stripePublishableKey={stripePublishableKey}
			terms={terms}
			title={title}
			valuePropsList={<Sisu.PremiumValuePropsList valueProps={valueProps} />}
			paymentPlan={paymentPlan}
			paymentMethod={<Sisu.PaymentMethod submitButtonLabel={submitButtonLabel} />}
		/>
	);
};

export const HandoffScreen = () => {
	const { formatMessage } = useIntl();

	return (
		<Sisu.HandoffScreen
			appImage={<Image alt="App image" fill src={appImage} style={{ objectFit: 'contain' }} unoptimized />}
			appStoreUrl={
				process.env.NEXT_PUBLIC_CALM_ENV === 'production'
					? 'https://link.sleep.calm.com/TH4H/subscriber'
					: 'https://calmsleepdev.onelink.me/Lfcx/upsell'
			}
			googlePlayStoreIcon={null}
			header={<Header />}
			qrCodeImage={
				<Image
					alt="Scan this QR code to download Calm Sleep mobile app."
					fill
					src={process.env.NEXT_PUBLIC_CALM_ENV === 'production' ? qrCodeImage : qrCodeImageDev}
					style={{ objectFit: 'contain' }}
					unoptimized
				/>
			}
			qrCodeInstructions={formatMessage(messages.qrCodeInstructions)}
			subtitle={formatMessage(messages.handoffSubheading)}
			title={formatMessage(messages.handoffHeading)}
		/>
	);
};

export const ErrorScreen = () => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	return (
		<Sisu.ErrorScreen
			header={<Header />}
			onScreenDidMount={() => {
				logEvent({
					eventName: 'Subscription : Upsell : Errored',
					eventProps: {
						error_message: 'Ineligible User',
						upsell_location: 'Web',
					},
					hydrateSleepEventProps: true,
				});
			}}
			title={formatMessage(messages.ineligibleErrorHeading)}
			subtitle={formatMessage(messages.ineligibleErrorSubheading, {
				a: chunk => <a href={getUrl('calm-sleep-app')}>{chunk}</a>,
			})}
		/>
	);
};
